<template>
  <v-container fluid class="list-page missing-invoice-page">
    <div class="list-page-heading">
      <div>
        <div class="list-page-heading__eyebrow">Billing</div>
        <h1>Missing tuition invoices</h1>
        <p>Active parents who have no positive invoice in the selected calendar month.</p>
      </div>
      <div class="list-page-heading__actions">
        <v-btn color="primary" depressed :loading="loadingInvoice" :disabled="!Customer.length" @click="createAllInvoice">
          <v-icon left>mdi-file-document-multiple-outline</v-icon>Create all missing
        </v-btn>
      </div>
    </div>

    <v-card :loading="loading" elevation="0" class="list-card">
      <div class="list-toolbar missing-invoice-page__toolbar">
        <v-text-field v-model="search" class="list-toolbar__search" outlined prepend-inner-icon="mdi-magnify"
          dense background-color="#F8FAF8" placeholder="Search missing parents" hide-details clearable />
        <div class="list-toolbar__actions">
          <v-menu v-model="monthMenu" :close-on-content-click="false" offset-y min-width="290px">
            <template #activator="{ on, attrs }">
              <v-text-field :value="selectedMonthLabel" class="missing-invoice-page__month" outlined dense readonly
                hide-details label="Billing month" prepend-inner-icon="mdi-calendar-month-outline" v-bind="attrs" v-on="on" />
            </template>
            <v-date-picker v-model="selectedMonth" type="month" no-title @input="monthChanged" />
          </v-menu>
          <v-btn icon outlined color="primary" :loading="loading" aria-label="Refresh missing invoices" @click="getList">
            <v-icon size="19">mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
      <div class="missing-invoice-page__summary">
        <span><strong>{{ Customer.length }}</strong> {{ Customer.length === 1 ? "parent is" : "parents are" }} missing an invoice</span>
        <span>{{ selectedMonthLabel }}</span>
      </div>
      <v-data-table :headers="headers" :items="Customer" :search="search" :loading="loading"
        :sort-by="sortBy" :page.sync="page" hide-default-footer :items-per-page="itemsPerPage">
        <template v-slot:item="{ item }">
          <tr class="pointer">
            <td class="text-center" @click="viewProfile(item)">#{{ item.localId || item.id }}</td>
            <td class="text-left" @click="viewProfile(item)">
              <span class="text-capitalize">{{ item.name }}</span>


            </td>
            <td class="text-center" @click="viewProfile(item)">
              {{ item.studentCount }}
            </td>
            <td class="text-right" @click="viewProfile(item)">
              {{ item.balance | currency }}


            </td>
            <td align="right">
              <v-btn text color="primary" :loading="loadingBtn" @click="createSingleInvoice(item)">
                <v-icon left small>mdi-file-document-plus-outline</v-icon>Create invoice
              </v-btn>
            </td>


          </tr>
        </template>
        <template #no-data>
          <div class="missing-invoice-page__empty">
            <v-icon color="success" size="34">mdi-check-circle-outline</v-icon>
            <strong>All scheduled parents have an invoice</strong>
            <span>No missing tuition invoices were found for {{ selectedMonthLabel }}.</span>
          </div>
        </template>
        <v-alert slot="no-results" :value="true" text type="info">No missing parents match “{{ search }}”.</v-alert>
      </v-data-table>
      <v-dialog v-model="dialog" persistent max-width="300px">
        <v-card grid-list-md>
          <ModelTitle title="Student Fee Invoice" @close="close()" />
          <v-card-text>
            <v-col cols="12" sm="12">
              <DateSelector v-model="selectedInvoiceDate" :initValue="selectedInvoiceDate" />
            </v-col>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="secondary" text @click="close()">Close</v-btn>
            <v-btn color="primary" :loading="loading" dark @click="submit()">Create</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-container>
</template>

<script>
import customerService from "@/modules/Customer/service.js";
import studentService from "@/modules/Student/service.js";
import invoiceService from "@/modules/Invoice/service.js";
import settingService from "@/modules/Setting/service.js";
import DateSelector from "@/components/DateSelector";
import { mapGetters } from 'vuex';

export default {
  name: "no-invoice-list",
  data() {
    return {
      selectedInvoiceDate: new Date(),
      selectedInvoice: null,
      selectedMonth: this.$moment().format("YYYY-MM"),
      monthMenu: false,
      singleInvoice: false,
      dialog: false,
      loadingInvoice: false,
      loading: false,
      Customer: [],
      createParentInvoiceDialog: false,
      search: null,
      page: 1,
      sortBy: "id",
      pageCount: 0,
      Invoice: {
        date: this.$moment().toISOString(),
      },
      loadingBtn: false,
      Students: [],
      sortDesc: false,
      selectedCustomer: null,
      itemsPerPage: 10,
      Setting: null,
      headers: [
        {
          text: "ID",
          value: "id",
          sortable: true,
          align: "center",
        },
        {
          text: "Name",
          value: "name",
          sortable: true,
          align: "left",
        },
        {
          text: "Student Count",
          value: "studentCount",
          sortable: true,
          align: "center",
        },
        {
          text: "Balance",
          value: "balance",
          sortable: true,
          align: "right",
        },
        {
          text: "Action",
          value: "id",
          sortable: false,
          align: "center",
        },
      ]
    }
  },
  components: {
    DateSelector,
  },
  computed: {
    ...mapGetters("global", ["currentBusiness"]),
    selectedMonthLabel() {
      return this.$moment(`${this.selectedMonth}-01`).format("MMMM YYYY");
    },
  },
  created() {
    document.title = "Taleem - Missing tuition invoices";
    return settingService.getById(this.currentBusiness.id).then((result) => {
      this.Setting = result.data;
      return this.getList();
    });
  },
  methods: {
    close() {
      this.dialog = !this.dialog
    },
    monthChanged() {
      this.monthMenu = false;
      this.getList();
    },
    defaultInvoiceDate() {
      const configuredDay = this.Setting && this.Setting.metadata && this.Setting.metadata.defaultDay;
      const billingDay = Number(configuredDay && typeof configuredDay === "object" ? configuredDay.id : configuredDay) || 1;
      const month = this.$moment(`${this.selectedMonth}-01`);
      return month.date(Math.min(billingDay, month.daysInMonth())).toISOString();
    },
    viewProfile(val) {
      this.$router.push({ path: `/parents/${val.id}/profile` });
    },
    createAllInvoice() {
      console.log('Customer Array:', this.Customer);
      if (!this.Setting.metadata.defaultDay || !this.Setting.metadata.defaultProduct) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 6000,
          })
          .fire({
            icon: "error",
            title: 'Default product is not set. Check the setting and try again '
          });
        return false
      }

      this.selectedInvoiceDate = this.defaultInvoiceDate()
      console.log('this.selectedInvoiceDate', this.selectedInvoiceDate)
        this.dialog = !this.dialog
      // for (let index = 0; index < this.Customer.length; index++) {
      //   const customer = this.Customer[index];
      //   console.log('customer', customer.id)
      //   await this.createInvoice(customer, true)
      // }
      // this.getList()
    },
    async submit() {
      if (this.singleInvoice) {
        this.createInvoice(this.selectedInvoice)
      } else {
        for (let index = 0; index < this.Customer.length; index++) {
          const customer = this.Customer[index];
          console.log('customer', customer.id)
          await this.createInvoice(customer, true)
        }
        this.getList()
      }

    },
    reset() {
      this.selectedInvoice = null
      this.singleInvoice = false
      this.dialog = !this.dialog
    },
    getPrice(setting) {
      return setting.parentPay + setting.charity
    },
    createSingleInvoice(val) {
      this.selectedInvoice = val
      this.singleInvoice = true
      this.selectedInvoiceDate = this.defaultInvoiceDate()
      this.dialog = !this.dialog
    },
    async createInvoice(item, all = false) {
      // console.log('ite.', item.id, this.Setting)
      try {
        this.loadingBtn = true
        this.selectedCustomer = (await customerService.getById(item.id)).data
        // console.log('selectedCustomer.', this.selectedCustomer)
        this.Students = (await studentService.getAll({ CustomerId: this.selectedCustomer.id, hideLeft: true })).data
        console.log('Students.', this.Students)
        if (this.Students.length === 0 ){
          this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "warning",
                title: `No Students found in ${this.selectedCustomer.fatherName} account.`,
              });
              this.loadingBtn = false;
              this.dialog = false;
              return false;
          

        }
        this.Invoice.CustomerId = this.selectedCustomer.id


        let detail = []
        this.Students.map(student => {
          let studentRow = {
            ProductId: this.Setting.metadata.defaultProduct.id,
            name: this.Setting.metadata.defaultProduct.name,
            studentName: student.fullName,
            price: this.getPrice(student.ClassStudents[0]),
            total: this.getPrice(student.ClassStudents[0]),
            parentPay: student.ClassStudents[0].parentPay,
            StudentId: student.id,
            qty: 1,
            cost: 0,
          }
          if (student && student.ClassRoomStudents.length) {
            studentRow.ClassRoomId = student.ClassRoomStudents[0].ClassRoomId
          }

          if (student && student.ClassStudents.length) {
            studentRow.ClassId = student.ClassStudents[0].Class.id
          }

          detail.push(studentRow)
        })

        this.Invoice.InvoiceDetails = detail

        this.Invoice.date = this.selectedInvoiceDate
        this.Invoice.dueDate = this.$moment(this.selectedInvoiceDate).add(4, 'days');
        // console.log('invoice', this.Invoice)
        this.loadingBtn = false
        return invoiceService
          .create(this.Invoice)
          .then(() => {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "success",
                title: `Invoice was created for ${this.selectedCustomer.fatherName} for ${this.Students.length} students.`,
              });
            if (!all) {
              this.getList()
            }
            this.reset()
            this.loadingBtn = false
          })
          .catch((error) => {
            this.loadingBtn = false
            console.log("error creating invoice", error);
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 6000,
              })
              .fire({
                icon: "error",
                title: error.data.message,
              });
          });
      } catch (error) {
        console.log('error', error)
        this.loadingBtn = false
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 6000,
          })
          .fire({
            icon: "error",
            title: error,
          });
      }

    },
    // invoiceCreated() {
    //   this.createParentInvoiceDialog = !this.createParentInvoiceDialog
    //   this.getList()
    // },
    getList() {
      this.loading = true;
      this.editDialog = false
      this.createDialog = false
      const selected = this.$moment(`${this.selectedMonth}-01`)
      let where = {
        start: selected.clone().startOf("month").toISOString(),
        end: selected.clone().endOf("month").toISOString(),
      }
      // if (this.CustomerId) {
      //   where.CustomerId = this.CustomerId;
      // }
      // if (this.ClassId) {
      //   where.ClassId = this.ClassId;
      // }
      console.log("where", where);
      return customerService.getAllNoInvoice(where).then((response) => {
        console.log('response', response)
        this.loading = false;
        this.Customer = response.data;
        this.itemsPerPage = this.Customer.length

      });
    },
  }
}
</script>

<style scoped>
.missing-invoice-page__toolbar {
  align-items: center;
}

.missing-invoice-page__month {
  width: 220px;
}

.missing-invoice-page__summary {
  display: flex;
  padding: 12px 20px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e7ece8;
  background: #f8faf8;
  color: #66736a;
  font-size: 13px;
}

.missing-invoice-page__summary strong {
  color: #253047;
  font-size: 18px;
}

.missing-invoice-page__empty {
  display: flex;
  min-height: 210px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: #758179;
}

.missing-invoice-page__empty strong {
  color: #253047;
  font-size: 16px;
}

@media (max-width: 600px) {
  .missing-invoice-page__toolbar,
  .missing-invoice-page__summary {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }

  .missing-invoice-page__month {
    width: 100%;
  }
}
</style>
